import axios from "axios";
import React, { useState } from "react";
import { setToken } from "../utils/staffAuth";

const pathURL = process.env.REACT_APP_API_URL;

/*
 * FXRY's palette (brand board, 2026), same values as FxryToaster.jsx. The
 * popup's stylesheet has no form styling of its own, so this screen carries
 * its own — it is the one screen that exists before the app does.
 */
const COLORS = {
  ivory: "#F4F3EE",
  darkIvory: "#D5D1C6",
  black: "#212322",
  charcoal: "#373C3B",
  golden: "#B88122",
};

/**
 * Sign this device in.
 *
 * Shown instead of the app when there is no staff token. It is the same
 * shared login as the old admin panel and superadmin v2 — one credential for
 * everyone, which is why the order itself records who checked it in
 * separately (utils/staffPrefs.js). A token lasts 30 days, so this is a
 * once-a-month screen, not a once-a-morning one.
 *
 * It deliberately says nothing about which accounts exist, and repeats the
 * backend's own wording for a bad password rather than inventing its own.
 */
function StaffLogin({ onSignedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await axios.post(`${pathURL}users/loginsuperadmin`, {
        email: email.trim(),
        password,
      });
      const token = response?.data?.token;
      if (!token) {
        throw new Error("That login didn't come back with a session.");
      }
      setToken(token);
      setPassword("");
      onSignedIn();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Couldn't sign in. Check the connection and try again.",
      );
      setBusy(false);
    }
  };

  const field = {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    marginTop: 6,
    border: `1px solid ${COLORS.darkIvory}`,
    borderRadius: 6,
    fontSize: 16, // 16px or iOS zooms the whole kiosk in on focus
    background: "#fff",
    color: COLORS.black,
    fontFamily: "inherit",
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        background: COLORS.ivory,
        // The checkout's own face. Without it this screen fell back to the
        // browser's serif (Calley, 2026-09-28: "font on the password screen
        // is odd"). Inputs and the button don't inherit fonts, hence
        // fontFamily: "inherit" on them too.
        fontFamily: "halyard-micro, system-ui, sans-serif",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: "100%",
          maxWidth: 360,
          background: "#fff",
          border: `1px solid ${COLORS.darkIvory}`,
          borderRadius: 10,
          padding: 24,
        }}
      >
        {/* App.css sets every h1 to Halyard Micro at 3rem with
            letter-spacing: -0.2rem. That tracking is tuned for a 48px display
            line; inherited at 22px it is roughly a sixth of the type size and
            closes the word gaps, so "Sign in to the counter" read as
            "Signintothecounter". Overridden here rather than in App.css
            because that rule is what every heading in the checkout is drawn
            with, and this screen is not the place to redraw them.

            Not "the counter": this same app runs at pop-ups (Hoste, Seattle)
            as well as in the studio, so the counter is not always a counter.
            Calley, 2026-09-20: "i hate the word counter". */}
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            lineHeight: 1.3,
            letterSpacing: "-0.01em",
            color: COLORS.black,
          }}
        >
          Sign in to FXRY checkout
        </h1>
        <p style={{ margin: "8px 0 20px", fontSize: 14, color: COLORS.charcoal }}>
          This device stays signed in for 30 days.
        </p>

        <label style={{ display: "block", fontSize: 14, color: COLORS.charcoal }}>
          Email
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            disabled={busy}
            style={field}
          />
        </label>

        <label
          style={{
            display: "block",
            marginTop: 16,
            fontSize: 14,
            color: COLORS.charcoal,
          }}
        >
          Password
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            disabled={busy}
            style={field}
          />
        </label>

        {error && (
          <p style={{ marginTop: 16, fontSize: 14, color: COLORS.golden }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          style={{
            width: "100%",
            marginTop: 24,
            padding: "14px 16px",
            border: "none",
            borderRadius: 6,
            background: COLORS.black,
            color: COLORS.ivory,
            fontSize: 16,
            fontWeight: 600,
            fontFamily: "inherit",
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </main>
  );
}

export default StaffLogin;
